import { useEffect, useState } from 'react';

enum FetchState {
  Idle = 'IDLE',
  Loading = 'LOADING',
  Refreshing = 'REFRESHING',
  Error = 'ERROR',
}

// similar to LS utils we have today but it's not typed, if someone wants to consolidate would be nice
function getCachedData<T>(key: string): T | null {
  const cachedItem = localStorage.getItem(key);
  if (!cachedItem) return null;
  try {
    const { data, expiry } = JSON.parse(cachedItem);
    if (expiry && expiry < Date.now()) {
      localStorage.removeItem(key);
      return null;
    }
    return data as T;
  } catch {
    return null;
  }
}

function setCachedData<T>(key: string, data: T, ttl?: number): void {
  if (!ttl) return;
  const expiry = Date.now() + ttl;
  const cachedItem = JSON.stringify({ data, expiry });
  localStorage.setItem(key, cachedItem);
}

// haha at what point am i just slowly building a budget verion of the SWR library; we should just use it
interface CacheAndNetworkOptions {
  skipCache?: boolean;
  skipDataFetch?: boolean;
  staleCacheTTL?: number;
  replaceStaleDataPostFetch?: boolean; // useful when you don't want to replace the UI & the change can wait
}

// sadly very similar to usePromise, but w/ Cache; inspired by Apollo Client (hooks)
function useCacheAndNetwork<T>(key: string, fetchDataFn: () => Promise<T>, options: CacheAndNetworkOptions = {}) {
  const { staleCacheTTL, replaceStaleDataPostFetch = true, skipCache = false, skipDataFetch = false } = options;
  const [data, setData] = useState<T | null>(() => (skipCache ? null : getCachedData<T>(key)));
  const [state, setState] = useState<FetchState>(data ? FetchState.Idle : FetchState.Loading);
  const [error, setError] = useState<string | null>(null);

  const hasData = data !== null;
  useEffect(() => {
    let isSubscribed = true;

    async function fetchAndCacheData() {
      setState(hasData ? FetchState.Refreshing : FetchState.Loading);
      setError(null);

      try {
        const freshData = await fetchDataFn();
        setCachedData(key, freshData, staleCacheTTL);
        if (isSubscribed) {
          if (!hasData || replaceStaleDataPostFetch === true) setData(freshData);
          setState(FetchState.Idle);
        }
      } catch (err) {
        if (isSubscribed) {
          setError((err as Error).message);
          setState(FetchState.Error);
        }
      }
    }

    if (!skipDataFetch) fetchAndCacheData();

    return () => {
      isSubscribed = false;
    };
  }, [key, fetchDataFn, hasData, skipDataFetch, staleCacheTTL, replaceStaleDataPostFetch]);

  return { data, state, error };
}

export { useCacheAndNetwork };
